/* App finale. Inventory patterns: AURUM revealEl / frame-batched motion, scoped here.
   Content is never CSS-hidden: failed JS, no observer and reduced motion stay readable. */
(() => {
  'use strict';
  const KEY = '__DivettaAppFinale';
  if (window[KEY]) { window[KEY].scan(document); return; }
  const selector = '[data-ld-z9-app]';
  const instances = new Map();
  const global = new AbortController();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 990px) and (hover: hover)');
  let pendingOpen = null;

  function mount(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const signal = abort.signal;
    const device = root.querySelector('[data-app-device]');
    const strength = Math.min(100, Math.max(0, Number(root.dataset.motionStrength) || 0)) / 100;
    const state = { abort, observer: null, frame: 0, visible: false, animations: new Set() };
    instances.set(root, state);
    const draw = () => {
      state.frame = 0;
      if (signal.aborted || !device) return;
      if (reduced.matches || !desktop.matches || document.hidden || !state.visible) {
        device.style.removeProperty('--app-float'); return;
      }
      const rect = root.getBoundingClientRect();
      const offset = Math.max(-1, Math.min(1, (innerHeight / 2 - rect.top - rect.height / 2) / innerHeight));
      device.style.setProperty('--app-float', `${(offset * 18 * strength).toFixed(2)}px`);
    };
    const queue = () => { if (!state.frame) state.frame = requestAnimationFrame(draw); };
    const stopMotion = () => {
      cancelAnimationFrame(state.frame); state.frame = 0;
      state.animations.forEach((a) => a.cancel()); state.animations.clear();
      device?.style.removeProperty('--app-float');
    };
    const reveal = () => {
      if (root.dataset.appRevealed === 'true') return;
      root.dataset.appRevealed = 'true';
      if (reduced.matches || window.Shopify?.designMode) return;
      root.querySelectorAll('[data-app-reveal]').forEach((el, index) => {
        if (!el.animate) return;
        const animation = el.animate([{opacity: .3, transform: 'translateY(18px)'}, {opacity: 1, transform: 'none'}], {duration: 540, delay: index * 80, easing: 'cubic-bezier(.22,1,.36,1)'});
        state.animations.add(animation);
        animation.finished.then(() => state.animations.delete(animation)).catch(() => {});
      });
    };
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(([entry]) => {
        state.visible = Boolean(entry?.isIntersecting);
        if (state.visible) reveal();
        queue();
      }, {threshold: 0, rootMargin: '80px 0px'});
      state.observer.observe(root);
    } else { state.visible = true; reveal(); }
    const resetForm = () => {
      const form = root.querySelector('form');
      form?.removeAttribute('aria-busy');
      form?.querySelector('button[type=submit]')?.removeAttribute('aria-disabled');
    };
    root.querySelector('form')?.addEventListener('submit', (event) => {
      const form = event.currentTarget;
      if (form.getAttribute('aria-busy') === 'true') { event.preventDefault(); return; }
      if (!form.checkValidity()) { event.preventDefault(); form.reportValidity(); return; }
      form.setAttribute('aria-busy', 'true');
      form.querySelector('button[type=submit]')?.setAttribute('aria-disabled', 'true');
      // Leave native Shopify submission, validation, errors and double opt-in untouched.
    }, {signal});
    window.addEventListener('scroll', () => { if (state.visible) queue(); }, {passive: true, signal});
    window.addEventListener('resize', queue, {passive: true, signal});
    window.addEventListener('pagehide', stopMotion, {signal});
    window.addEventListener('pageshow', () => { resetForm(); queue(); }, {signal});
    document.addEventListener('visibilitychange', () => { if (document.hidden) stopMotion(); else queue(); }, {signal});
    reduced.addEventListener('change', () => { stopMotion(); queue(); }, {signal});
    desktop.addEventListener('change', () => { stopMotion(); queue(); }, {signal});
    state.destroy = () => { abort.abort(); state.observer?.disconnect(); stopMotion(); resetForm(); };
    const feedback = root.querySelector('[data-app-feedback]');
    if (feedback) requestAnimationFrame(() => { if (!signal.aborted) feedback.focus(); });
  }

  function scan(scope = document) {
    instances.forEach((state, root) => { if (!root.isConnected) { state.destroy(); instances.delete(root); } });
    if (scope.matches?.(selector)) mount(scope);
    scope.querySelectorAll?.(selector).forEach(mount);
    if (pendingOpen) { const detail = pendingOpen; pendingOpen = null; open(detail); }
  }

  function open(detail = {}) {
    if (window.Shopify?.designMode) return;
    const root = Array.from(instances.keys()).find((node) => node.isConnected);
    if (!root) { pendingOpen = detail; return; }
    const automatic = detail.source === 'home-finale';
    // Back/refresh must restore the visitor's place, not replay an automatic finale jump.
    const navigation = performance.getEntriesByType?.('navigation')?.[0]?.type;
    if (automatic && (root.dataset.appAutoOpened === 'true' || window.DV2HomeReturn?.isRestoring?.() || navigation === 'back_forward')) return;
    if (automatic) root.dataset.appAutoOpened = 'true';
    root.dataset.appRevealed = 'true';
    const film = window.DV2Home?.getInstance?.();
    if (film && !film.destroyed) film.journeyReleased = true;
    root.scrollIntoView({behavior: reduced.matches ? 'auto' : 'smooth', block: 'start'});
    if (!automatic) root.querySelector('h2')?.focus({preventScroll: true});
  }

  document.addEventListener('ld:app-preview-open', (event) => open(event.detail), {signal: global.signal});
  document.addEventListener('shopify:section:load', (event) => scan(event.target), {signal: global.signal});
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((state, root) => {
      if (event.target === root || event.target.contains?.(root)) { state.destroy(); instances.delete(root); }
    });
  }, {signal: global.signal});
  window[KEY] = {scan, destroy() { global.abort(); instances.forEach((s) => s.destroy()); instances.clear(); delete window[KEY]; }};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), {once: true, signal: global.signal});
  else scan();
})();
